import React, { useMemo, useEffect, useRef, useState } from "react";
import { AlertTriangle, Lightbulb } from "lucide-react";

/* ---------- helpers (unchanged) ---------- */
const toNumber = (v) =>
  v == null
    ? 0
    : typeof v === "number"
    ? v
    : parseFloat(String(v).replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;

const netFromRow = (r) =>
  toNumber(r.K_15) + toNumber(r.K_19) + toNumber(r.K_10) +
  toNumber(r.K_21) + toNumber(r.K_17) + toNumber(r.K_11) + toNumber(r.K_22);

const getYearMonth = (row) => {
  if (row?.Rok && row?.Miesiac) return `${row.Rok}-${String(row.Miesiac).padStart(2, "0")}`;
  const d = row?.DataWystawienia ? new Date(row.DataWystawienia) : null;
  if (!d || isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const prevYm = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const parseDate = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d : null; };
const fmtPLN0 = (n) => (n ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " zł";
const deltaPct = (curr, prev) => (prev === 0 ? (curr > 0 ? Infinity : 0) : (curr - prev) / prev);
const isCorrection = (r) => /KOR|KOREK/i.test(r?.DowodSprzedazy || "") || netFromRow(r) < 0;

/* small UI bits */
const Separator = () => <div style={{ height: 1, background: "#e5e7eb", margin: "8px 0 12px" }} />;

const Row = ({ children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      lineHeight: 1.5,
      color: "#dc2626",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        display: "grid",
        placeItems: "center",
        borderRadius: 6,
        background: "#fef2f2",
        color: "#dc2626",
        flexShrink: 0,
      }}
    >
      <AlertTriangle size={14} />
    </div>
    <div style={{ fontSize: 16, fontWeight: 600 }}>{children}</div>
  </div>
);


/* ---------- component (alerts only, 1 per page, auto-rotate 5s) ---------- */
const AlertsAI = ({
  salesData = [],
  title = "Alerty",
  titleIcon: TitleIcon = Lightbulb,
  pageSize = 1,          // <-- changed from 3 to 1
  autoRotateMs = 5000,
}) => {
  const alertsAll = useMemo(() => {
    const byYm = new Map();
    (salesData || []).forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym) return;
      if (!byYm.has(ym)) byYm.set(ym, []);
      byYm.get(ym).push(r);
    });
    const yms = [...byYm.keys()].sort();
    const ymCurr = yms[yms.length - 1] || null;
    const ymPrev = ymCurr ? prevYm(ymCurr) : null;
    const rowsCurr = ymCurr ? (byYm.get(ymCurr) || []) : [];
    const rowsPrev = ymPrev ? (byYm.get(ymPrev) || []) : [];

    /* 1) Brak aktywności klienta X (>3m) */
    const lastSaleByClient = new Map();
    (salesData || []).forEach((r) => {
      const c = r.NazwaKontrahenta || r.NrKontrahenta;
      if (!c) return;
      const d = parseDate(r.DataWystawienia);
      if (!d) return;
      const prev = lastSaleByClient.get(c);
      if (!prev || d > prev) lastSaleByClient.set(c, d);
    });
    const currMonthDate = ymCurr ? new Date(ymCurr + "-01") : null;
    let inactiveClient = null;
    if (currMonthDate) {
      const monthsDiff = (from, to) =>
        (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
      lastSaleByClient.forEach((last, client) => {
        const months = monthsDiff(last, currMonthDate);
        if (months >= 3) {
          if (!inactiveClient || months > inactiveClient.months)
            inactiveClient = { client, last, months };
        }
      });
    }

    /* 2) Najwyższa wartość faktury w miesiącu */
    let maxInv = null;
    rowsCurr.forEach((r) => {
      const val = netFromRow(r);
      if (val > 0 && (!maxInv || val > maxInv.value))
        maxInv = { value: val, cust: r.NazwaKontrahenta || r.NrKontrahenta || "—" };
    });

    /* 3) Średnia faktura + % vs poprzedni miesiąc */
    const sum = (arr, acc) => arr.reduce((s, r) => s + acc(r), 0);
    const currSum = sum(rowsCurr, netFromRow);
    const prevSum = sum(rowsPrev, netFromRow);
    const currCnt = rowsCurr.length || 0;
    const prevCnt = rowsPrev.length || 0;
    const avgCurr = currCnt ? currSum / currCnt : 0;
    const avgPrev = prevCnt ? prevSum / prevCnt : 0;
    const avgDelta = deltaPct(avgCurr, avgPrev);

    /* 4) Najlepszy klient w miesiącu */
    const byClient = new Map();
    rowsCurr.forEach((r) => {
      const c = r.NazwaKontrahenta || r.NrKontrahenta || "—";
      byClient.set(c, (byClient.get(c) || 0) + netFromRow(r));
    });
    let bestClient = null;
    byClient.forEach((v, k) => {
      if (!bestClient || v > bestClient.sum) bestClient = { client: k, sum: v };
    });

    /* 5) % więcej korekt m/m */
    const currCorr = rowsCurr.filter(isCorrection).length;
    const prevCorr = rowsPrev.filter(isCorrection).length;
    const corrDelta = deltaPct(currCorr, prevCorr);

    /* 6) Kontrahent powrócił po ≥3 mies. */
    let returned = null;
    if (ymCurr) {
      const prevSaleByClient = new Map();
      yms
        .filter((m) => m < ymCurr)
        .forEach((m) => {
          (byYm.get(m) || []).forEach((r) => {
            const c = r.NazwaKontrahenta || r.NrKontrahenta;
            if (!c) return;
            const d = parseDate(r.DataWystawienia);
            if (!d) return;
            const prev = prevSaleByClient.get(c);
            if (!prev || d > prev) prevSaleByClient.set(c, d);
          });
        });
      const monthsDiff = (from, to) =>
        (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
      const currStart = new Date(ymCurr + "-01");
      const seen = new Set();
      rowsCurr.forEach((r) => {
        const c = r.NazwaKontrahenta || r.NrKontrahenta;
        if (!c || seen.has(c)) return;
        seen.add(c);
        const lastBefore = prevSaleByClient.get(c);
        if (!lastBefore) return; // first-timer, not a "return"
        const months = monthsDiff(lastBefore, currStart);
        if (months >= 3) {
          if (!returned || months > returned.months) returned = { client: c, months };
        }
      });
    }

    /* build 6 alert strings */
    const items = [];
    if (inactiveClient) {
      items.push(
        <>
          Brak aktywności klienta <strong>{inactiveClient.client}</strong> od{" "}
          <strong>{inactiveClient.last.toLocaleDateString("pl-PL")}</strong> –{" "}
          <strong>powyżej {inactiveClient.months} mies.</strong>
        </>
      );
    } else items.push(<>Brak alertu o nieaktywności powyżej 3 miesięcy.</>);

    if (maxInv) {
      items.push(
        <>
          Najwyższa wartość faktury w tym miesiącu to{" "}
          <strong>{fmtPLN0(maxInv.value)}</strong> dla{" "}
          <strong>{maxInv.cust}</strong>.
        </>
      );
    } else items.push(<>Brak faktur w bieżącym miesiącu.</>);

    if (currCnt > 0) {
      const trendWord =
        avgDelta === Infinity
          ? "większa"
          : avgDelta === 0
          ? "taka sama jak"
          : avgDelta > 0
          ? "większa"
          : "mniejsza";
      const pct =
        avgDelta === Infinity
          ? "— (z 0 zł)"
          : `${Math.abs(avgDelta * 100).toFixed(1)}%`;
      items.push(
        <>
          Średnia faktura sprzedaży wynosi <strong>{fmtPLN0(avgCurr)}</strong> i
          jest <strong>{trendWord}</strong> o <strong>{pct}</strong> w
          porównaniu z poprzednim miesiącem.
        </>
      );
    } else items.push(<>Brak średniej wartości (brak faktur w bieżącym miesiącu).</>);

    if (bestClient && bestClient.sum > 0) {
      items.push(
        <>
          Najlepszy klient w tym miesiącu to{" "}
          <strong>{bestClient.client}</strong> ze sprzedażą{" "}
          <strong>{fmtPLN0(bestClient.sum)}</strong>.
        </>
      );
    } else items.push(<>Brak najlepszego klienta (brak sprzedaży w bieżącym miesiącu).</>);

    if (currCorr > 0 || prevCorr > 0) {
      if (corrDelta === Infinity) {
        items.push(
          <>
            W tym miesiącu pojawiły się korekty (poprzednio{" "}
            <strong>0</strong>) – teraz <strong>{currCorr}</strong>{" "}
            dokumentów.
          </>
        );
      } else {
        const word = corrDelta >= 0 ? "więcej" : "mniej";
        items.push(
          <>
            W tym miesiącu jest o{" "}
            <strong>{Math.abs(corrDelta * 100).toFixed(1)}%</strong> {word} korekt
            niż w poprzednim ( {prevCorr} ➜ {currCorr} ).
          </>
        );
      }
    } else items.push(<>Brak korekt w bieżącym i poprzednim miesiącu.</>);

    if (returned) {
      items.push(
        <>
          Kontrahent <strong>{returned.client}</strong> powrócił po{" "}
          <strong>{returned.months}</strong> miesiącach (min. 3).
        </>
      );
    } else items.push(<>Brak powrotów po co najmniej 3 miesiącach przerwy.</>);

    return items;
  }, [salesData]);

  /* pagination + auto-rotate */
  const groups = Math.max(1, Math.ceil(alertsAll.length / pageSize));
  const [groupIdx, setGroupIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (autoRotateMs <= 0 || groups <= 1) return;
    timerRef.current = setInterval(
      () => setGroupIdx((i) => (i + 1) % groups),
      autoRotateMs
    );
    return () => clearInterval(timerRef.current);
  }, [groups, autoRotateMs]);

  const start = groupIdx * pageSize;
  const end = Math.min(start + pageSize, alertsAll.length);
  const pageItems = alertsAll.slice(start, end);

  return (
    <div style={{ background: "#fff", padding: 16 }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: "#f1f5f9",
            display: "grid",
            placeItems: "center",
            color: "#0f172a",
          }}
        >
          <TitleIcon size={16} />
        </div>
        <div style={{ fontWeight: 800, color: "#0f172a" }}>{title}</div>
      </div>

      <Separator />

      {/* current page */}
      <div style={{ display: "grid", gap: 10 }}>
        {pageItems.map((node, idx) => (
          <Row key={`${groupIdx}-${idx}`}>{node}</Row>
        ))}

        {/* placeholders to keep card height fixed */}
        {Array.from({ length: Math.max(0, pageSize - pageItems.length) }).map(
          (_, i) => (
            <div
              key={`ph-${groupIdx}-${i}`}
              style={{ visibility: "hidden" }} // takes space, not visible
            >
              <Row>&nbsp;</Row>
            </div>
          )
        )}
      </div>

      {/* dots */}
      {groups > 1 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            justifyContent: "center",
            marginTop: 12,
          }}
        >
          {Array.from({ length: groups }).map((_, i) => (
            <button
              key={i}
              onClick={() => setGroupIdx(i)}
              aria-label={`Strona ${i + 1}`}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                background: i === groupIdx ? "#dc2626" : "#e5e7eb",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsAI;
